import CloseIcon from '@mdi/svg/svg/close.svg?react';
import ImageIcon from '@mdi/svg/svg/image-plus-outline.svg?react';
import UploadIcon from '@mdi/svg/svg/upload.svg?react';
import { MDIIcon } from '@solinkcorp/solink-components';
import React from 'react';
import { useDropzone } from 'react-dropzone';
import useLocale from 'src/hooks/useLocale';
import styles from './SearchImagePanel.module.sass';

export const MAX_IMAGE_SIZE_MB = 5;
export const MAX_IMAGE_SIZE_BYTES = MAX_IMAGE_SIZE_MB * 1024 * 1024;
export const ACCEPTED_IMAGE_TYPES: Record<string, string[]> = {
  'image/jpeg': ['.jpg', '.jpeg'],
  'image/png': ['.png'],
};

interface SearchImagePanelProps {
  onSelect: (file: File) => void;
  /** URL of the currently selected image to preview once its upload has completed. */
  previewUrl?: string | null;
  /** Clears the active image search. Shown as a button in the preview state. */
  onClear?: () => void;
  className?: string;
}

/**
 * Drop panel shown below the search bar when it is focused. Prompts the user to
 * drag-and-drop or pick a JPG/PNG image (up to 5MB) to search by. Once an image
 * has been uploaded it previews that image with controls to clear the search or
 * upload a different file.
 */
export default function SearchImagePanel({
  onSelect,
  previewUrl,
  onClear,
  className,
}: SearchImagePanelProps) {
  const locale = useLocale();
  const [error, setError] = React.useState<string | null>(null);

  const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
    accept: ACCEPTED_IMAGE_TYPES,
    maxFiles: 1,
    maxSize: MAX_IMAGE_SIZE_BYTES,
    multiple: false,
    // Disable click/keyboard on the root so it doesn't fire while previewing an
    // image; the "upload new file" button calls `open()` explicitly instead.
    noClick: !!previewUrl,
    noKeyboard: !!previewUrl,
    onDrop: (acceptedFiles, fileRejections) => {
      if (fileRejections.length > 0) {
        const code = fileRejections[0]?.errors[0]?.code;
        setError(
          code === 'file-too-large'
            ? locale.videoSearch.imageTooLarge
            : locale.videoSearch.imageWrongType,
        );
        return;
      }

      const file = acceptedFiles[0];
      if (file) {
        setError(null);
        onSelect(file);
      }
    },
  });

  return (
    <div
      data-testid="search-image-panel"
      className={`${styles['search-image-panel']} ${className ?? ''}`}
    >
      <div
        {...getRootProps()}
        className={`${styles['search-image-dropzone']} ${
          isDragActive ? styles['search-image-dropzone--active'] : ''
        } ${previewUrl ? styles['search-image-dropzone--preview'] : ''}`}
      >
        <input {...getInputProps()} data-testid="search-image-file-input" />
        {previewUrl ? (
          <img src={previewUrl} alt="search" className={styles['search-image-preview-img']} />
        ) : (
          <>
            <MDIIcon icon={ImageIcon} size={28} fill="var(--color-turquoise-light)" />
            <p className={styles['search-image-prompt']}>{locale.videoSearch.imageDropPrompt}</p>
            <p className={styles['search-image-hint']}>{locale.videoSearch.imageDropHint}</p>
          </>
        )}
      </div>
      {previewUrl && (
        <div className={styles['search-image-actions']}>
          <button
            type="button"
            data-testid="search-image-clear-search"
            className={`${styles['search-image-action-button']} ${styles['search-image-action-button--clear']}`}
            onClick={onClear}
          >
            <MDIIcon icon={CloseIcon} size={16} />
            {locale.videoSearch.imageClearSearch}
          </button>
          <button
            type="button"
            data-testid="search-image-upload-new"
            className={`${styles['search-image-action-button']} ${styles['search-image-action-button--upload']}`}
            onClick={open}
          >
            <MDIIcon icon={UploadIcon} size={16} />
            {locale.videoSearch.imageUploadNew}
          </button>
        </div>
      )}
      {error && <p className={styles['search-image-error']}>{error}</p>}
    </div>
  );
}
